/*
 * The entry point. Three providers, in an order that is not arbitrary:
 *
 *   I18nProvider     outermost, because everything below it renders copy — including the error
 *                    states of the two providers underneath.
 *   QueryClient      the single data-fetching idiom (technology-selection §9).
 *   SessionProvider  third, because it renders copy and issues queries.
 *   ScopeProvider    fourth, because the scopes it may offer come from the session it sits
 *                    inside — a selector that rendered before the principal was known would have
 *                    to guess, and guessing wide is a disclosure.
 *   ShortcutProvider sixth, because a shortcut is a screen's action reached by a key, so it must
 *                    sit inside everything a screen's action can need. Nothing above it listens
 *                    for keys, which is what keeps the sign-in screen free of them.
 *   CopilotProvider  innermost, because what the copilot is about is declared by whichever screen
 *                    is showing — it holds a stack the screens push onto, so it must sit under
 *                    every screen and over the shell that reads it.
 *
 * The stylesheet is imported here and only here. A component importing it would make styling a
 * side effect of whichever module happened to load first.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nProvider } from '@indovation/i18n/react';
import '@indovation/design-system/styles.css';
import { App } from './app.js';
import { SessionProvider } from './auth/session.js';
import { ScopeProvider } from './scope/scope.js';
import { ShortcutProvider } from './keyboard/shortcuts.js';
import { CopilotProvider } from './copilot/context.js';
import { SideSurfaceProvider } from './app/side-surface.js';
import { Toasts } from './app/toasts.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or 403 will not become a 200 by asking again, and retrying an expired credential
      // three times just delays the redirect to sign-in.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) {
          return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('index.html has no #root element.');
}

createRoot(container).render(
  <StrictMode>
    <I18nProvider>
      {/* Second, directly under the catalogue it reads and over everything that can save: a toast
          outlives the screen that raised it, so it must sit above the router rather than inside a
          panel that unmounts the moment an editor closes. */}
      <Toasts>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <ScopeProvider>
              <ShortcutProvider>
                <CopilotProvider>
                  {/* Outside the router and inside the copilot's own provider: the register has to
                      outlive a navigation, and the copilot is one of the two things it arbitrates. */}
                  <SideSurfaceProvider>
                    <App />
                  </SideSurfaceProvider>
                </CopilotProvider>
              </ShortcutProvider>
            </ScopeProvider>
          </SessionProvider>
        </QueryClientProvider>
      </Toasts>
    </I18nProvider>
  </StrictMode>,
);
